import React from 'react';
import { getSupabaseClient } from '../supabaseClient';

type UserRow = {
  id: string;
  email: string;
  nickname: string;
  name: string | null;
  city: string | null;
  role: 'superadmin' | 'admin' | 'booklover';
  created_at: string;
  last_login_at: string | null;
  book_count: number;
  request_count: number;
};

export default function Users() {
  const supabase = getSupabaseClient();
  const [users, setUsers] = React.useState<UserRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const loadUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      // Load users with their book and request counts
      const { data: usersData, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Load book counts for each user
      const usersWithStats = await Promise.all(
        (usersData || []).map(async (user: any) => {
          const [{ count: bookCount }, { count: requestCount }] = await Promise.all([
            supabase
              .from('books')
              .select('*', { count: 'exact', head: true })
              .eq('owner_id', user.id),
            supabase
              .from('requests')
              .select('*', { count: 'exact', head: true })
              .or(`owner_id.eq.${user.id},reader_id.eq.${user.id}`),
          ]);

          return {
            ...user,
            book_count: bookCount || 0,
            request_count: requestCount || 0,
          };
        })
      );

      setUsers(usersWithStats);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.city && user.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const deactivateUser = async () => {
    if (!confirm('Are you sure you want to deactivate this user? This will prevent them from logging in.')) {
      return;
    }

    try {
      // In a real implementation, you would have a user status field or use Supabase auth admin API
      // For now, we'll just show a message
      alert('User deactivation would be implemented here. This requires Supabase Auth Admin API access.');
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      alert('Failed to deactivate user');
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      alert(`Password reset email sent to ${email}`);
    } catch (error) {
      console.error('Failed to send password reset:', error);
      alert('Failed to send password reset email');
    }
  };

  return (
    <div>
      <h1 className="page-header">
        User Management
      </h1>
      <div className="page-subtitle">
        Manage users and their accounts
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>All Users</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button onClick={loadUsers}>Refresh</button>
          </div>
        </div>

        {loading ? (
          <div>Loading users...</div>
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <strong>{filteredUsers.length}</strong> user(s) found
              {searchTerm && (
                <span className="muted" style={{ marginLeft: '8px' }}>
                  (filtered from {users.length} total users)
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="table-header-row">
                    <th className="table-cell">Email</th>
                    <th className="table-cell">Nickname</th>
                    <th className="table-cell">Name</th>
                    <th className="table-cell">City</th>
                    <th className="table-cell">Role</th>
                    <th className="table-cell">Books</th>
                    <th className="table-cell">Requests</th>
                    <th className="table-cell">Joined</th>
                    <th className="table-cell">Last Login</th>
                    <th className="table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="table-data-row">
                      <td className="table-cell">{user.email}</td>
                      <td className="table-cell">{user.nickname}</td>
                      <td className="table-cell">{user.name || '—'}</td>
                      <td className="table-cell">{user.city || '—'}</td>
                      <td className="table-cell">
                        <span className={`badge ${
                          user.role === 'superadmin' ? 'badge-superadmin' :
                          user.role === 'admin' ? 'badge-admin' :
                          'badge-booklover'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="table-cell-center">
                        <span className={`badge-count ${
                          user.book_count > 0 ? 'badge-count-positive' : 'badge-count-zero'
                        }`}>
                          {user.book_count}
                        </span>
                      </td>
                      <td className="table-cell-center">
                        <span className={`badge-count ${
                          user.request_count > 0 ? 'badge-count-positive' : 'badge-count-zero'
                        }`}>
                          {user.request_count}
                        </span>
                      </td>
                      <td className="table-cell">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="table-cell">
                        {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="table-cell">
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => sendPasswordReset(user.email)}
                            className="action-button-small action-button-reset"
                          >
                            Reset PW
                          </button>
                          <button
                            onClick={() => deactivateUser()}
                            className="action-button-small action-button-deactivate"
                          >
                            Deactivate
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>User Statistics</h2>
        <div className="stats-grid">
          <div className="metric-card">
            <div className="metric-value stats-card-value-primary">{users.length}</div>
            <div className="metric-label">Total Users</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-success">
              {users.filter(u => u.book_count > 0).length}
            </div>
            <div className="metric-label">Active Book Owners</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-info">
              {users.filter(u => u.request_count > 0).length}
            </div>
            <div className="metric-label">Active Readers</div>
          </div>
        </div>
      </div>
    </div>
  );
}